import type { Key } from "react-aria-components";
import { comboboxCreateKey } from "./comboboxItems.js";

/** Delimiters a pasted list is split on: comma, semicolon, newline or tab. */
const pastedListSeparator = /[,;\n\t]+/;

/**
 * Splits pasted text into the values it lists, or returns null when it holds at
 * most one — a single value belongs in the filter input, where it can be
 * reviewed and completed against the options like anything typed.
 */
export const readPastedList = (text: string): string[] | null => {
  const values = text
    .split(pastedListSeparator)
    .map((value) => value.trim())
    .filter((value) => value !== "");

  return values.length > 1 ? values : null;
};

/**
 * What a pasted value has to equal to name this option, and the key it names.
 */
export interface PastedListOption {
  key: Key;
  /** Anything that isn't a string is ignored, so a rendered element can be passed. */
  texts: readonly unknown[];
}

/**
 * The part of react-stately's collection this module walks. Structural on
 * purpose: it is satisfied by `state.selectionManager.collection`, which —
 * unlike `state.collection` — is never the filtered one.
 */
interface OptionCollection {
  [Symbol.iterator](): Iterator<OptionNode>;
  getChildren?: (key: Key) => Iterable<OptionNode>;
}

interface OptionNode {
  type: string;
  key: Key;
  /** The item object a dynamic collection was built from; absent for static children and sections. */
  value: unknown;
  /** react-aria's own match text for the option. */
  textValue: string;
}

/**
 * Every option a pasted value may name, read from the collection react-aria
 * built rather than from the items the caller passed: it is flat about
 * sections, carries the keys react-aria actually assigned (which an `id` on a
 * rendered {@link ComboboxItem} changes), and holds static children too.
 *
 * `getText` renders an option's chip content, which is what a value pasted
 * from an existing chips field would say.
 */
export const readPasteOptions = (
  collection: OptionCollection,
  getText: (item: object) => unknown,
): PastedListOption[] => {
  const options: PastedListOption[] = [];

  const visit = (nodes: Iterable<OptionNode>) => {
    for (const node of nodes) {
      if (node.type !== "item") {
        // A section is not selectable; its options are the nodes below it.
        visit(collection.getChildren?.(node.key) ?? []);
        continue;
      }
      // The synthetic "Add ..." option is this widget's own, not something a
      // pasted value may name.
      if (node.key === comboboxCreateKey) {
        continue;
      }
      options.push({
        key: node.key,
        texts: [
          node.textValue,
          node.value == null ? undefined : getText(node.value),
          String(node.key),
        ],
      });
    }
  };
  visit(collection);

  return options;
};

export interface PastedListMatch {
  /** Keys to select, in pasted order. Excludes what is selected or disabled already. */
  added: Key[];
  /** Values naming no option at all, deduplicated. */
  unmatched: string[];
}

// Matched the way typing is: ignoring case, accents and whitespace, so a value
// copied out of a spreadsheet still finds "John Smith" when it says "johnsmith"
// or "Jose" when it says "José". (Typing runs through a locale collator; this
// is its equality-only equivalent, which a lookup table needs.)
const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/\s+/g, "")
    .toLowerCase();

/**
 * Resolves pasted values against a combobox's options, matching an option by
 * its text or by its key — a copied list can hold either, and the select this
 * family replaced accepted either.
 *
 * Where a value could name two options the earlier one wins.
 */
export const matchPastedList = (
  values: readonly string[],
  options: {
    options: readonly PastedListOption[];
    isDisabled: (key: Key) => boolean;
    isSelected: (key: Key) => boolean;
  },
): PastedListMatch => {
  const { options: pasteOptions, isDisabled, isSelected } = options;

  // One pass over the options rather than a scan per pasted value: a pasted
  // list can be hundreds of rows.
  const keyByValue = new Map<string, Key>();
  for (const option of pasteOptions) {
    for (const text of option.texts) {
      if (typeof text !== "string" || text === "") {
        continue;
      }
      const lookup = normalize(text);
      if (lookup !== "" && !keyByValue.has(lookup)) {
        keyByValue.set(lookup, option.key);
      }
    }
  }

  const added: Key[] = [];
  const unmatched: string[] = [];
  const takenKeys = new Set<string>();
  // Unmatched values keep their own spelling: they are the caller's to create,
  // and two that differ by more than case are two values.
  const takenValues = new Set<string>();
  for (const value of values) {
    const key = keyByValue.get(normalize(value));

    if (key === undefined) {
      const lookup = value.toLowerCase();
      if (!takenValues.has(lookup)) {
        takenValues.add(lookup);
        unmatched.push(value);
      }
      continue;
    }

    // A value naming an option that is already selected — or that can't be
    // selected at all — is still a match; it just has nothing to add.
    if (takenKeys.has(String(key)) || isSelected(key) || isDisabled(key)) {
      continue;
    }
    takenKeys.add(String(key));
    added.push(key);
  }

  return { added, unmatched };
};
